<script setup>
import { computed, ref } from 'vue'
import dayjs from 'dayjs'
import { calendarDays, holidayBars, planningStatusLabels, holidayPlanningColor } from './holidayCalendar'
import TeamHolidayStatus from '../team-holidays/TeamHolidayStatus.vue'
import { canChangeHolidayStatus } from '../team-holidays/teamHolidayStatus'
import 'dayjs/locale/es'

dayjs.locale('es')

const props = defineProps({
  actionable: { type: Boolean, default: false },
  vacationPlanning: {
    type: Array,
    required: true,
    default: () => [],
  },
  fromDate: {
    type: String,
    required: true,
  },
  toDate: {
    type: String,
    required: true,
  },
})

const emit = defineEmits(['status-change'])

const hierarchical = computed(() => Array.isArray(props.vacationPlanning) && props.vacationPlanning.some(employee => employee.depth != null))
const employees = computed(() => (Array.isArray(props.vacationPlanning) ? props.vacationPlanning : []).map((employee, index, list) => {
  const guides = (employee.guides || []).slice(0, 5).map(guide => ({ ...guide, continues: (list[index + 1]?.depth || 0) > guide.level }))
  return { ...employee, guides, bars: holidayBars(employee.holidays, rangeOfDays.value) }
}))

const dialogHolidayInfo = ref(false);
const selectedHoliday = ref();
const selectedEmployee = ref();

const rangeOfDays = computed(() => calendarDays(props.fromDate, props.toDate))

const groupedDaysByMonth = computed(() => {
  const groups = {}
  rangeOfDays.value.forEach((day) => {
    const key = day.format('YYYY-MM')
    if (!groups[key]) {
      groups[key] = []
    }
    groups[key].push(day)
  })

  return Object.keys(groups).sort().map((monthKey) => {
    const daysInMonth = groups[monthKey]
    const firstDay = daysInMonth[0]
    const monthName = firstDay.format('MMMM YYYY')
    return {
      monthKey,
      monthName: capitalizeFirstLetter(monthName),
      days: daysInMonth
    }
  })
})

const capitalizeFirstLetter = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

const isWeekend = (day) => {
  const dayOfWeek = day.day()
  return dayOfWeek === 0 || dayOfWeek === 6
}

const showHolidayDetails = (holiday, employee) => {
  selectedEmployee.value = employee;
  dialogHolidayInfo.value = true;
  selectedHoliday.value = holiday;
}

const closeDialogHolidayInfo = () => {
  dialogHolidayInfo.value = false;
  selectedHoliday.value = null;
  selectedEmployee.value = null;
}

const requestStatusChange = (status) => {
  const holiday = selectedHoliday.value?.details;
  if (!props.actionable || !canChangeHolidayStatus(holiday, status)) return;
  const employee = selectedEmployee.value;
  emit('status-change', {
    employee: employee.employee || { id: employee.id, name: employee.name, fullName: employee.name },
    holiday,
  }, status);
  closeDialogHolidayInfo();
}

</script>

<template>
  <div class="holiday-planning mt-5">
    <div class="holiday-legend" aria-label="Estados de las solicitudes">
      <span v-for="(label, status) in planningStatusLabels" :key="status"><i :style="{ backgroundColor: holidayPlanningColor(status) }" aria-hidden="true" />{{ label }}</span>
    </div>
    <div class="holiday-grid-container">
    <div class="table-wrapper">
      <div class="gantt-container" :class="{ 'gantt-hierarchy': hierarchical }" :style="{ '--day-count': rangeOfDays.length }">
        <table>
          <colgroup>
            <col class="employee-col">
            <col v-for="day in rangeOfDays" :key="day.format('YYYY-MM-DD')" class="day-col">
          </colgroup>
          <thead class="sticky-header">
            <tr>
              <th class="employee-column" rowspan="2">
                Empleado
              </th>
              <th v-for="monthGroup in groupedDaysByMonth" :key="monthGroup.monthKey" :colspan="monthGroup.days.length"
                class="month-header">
                {{ monthGroup.monthName }}
              </th>
            </tr>
            <tr>
              <th v-for="day in rangeOfDays" :key="day.format('YYYY-MM-DD')" class="day-column">
                <div class="day-container">
                  <span class="day-number">{{ day.format('DD') }}</span>
                  <span class="day-name">{{ day.format('ddd') }}</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="employee in employees" :key="employee.id" class="employee-row" :class="{ 'employee-manager': employee.isTeamLeader, 'employee-root': employee.depth === 0 }">
              <td class="employee-name">
                <div class="employee-name-content" :class="{ 'employee-hierarchy': employee.depth != null }" :style="employee.depth != null ? { '--employee-depth': Math.min(employee.depth, 5) } : undefined" :title="employee.managerName ? `${employee.name} · Responsable: ${employee.managerName}` : employee.name">
                  <span v-for="guide in (employee.guides || [])" :key="guide.level" class="employee-guide" :class="{ 'guide-continues': guide.continues, 'guide-last': guide.level === Math.min(employee.depth, 5) - 1 }" :style="{ '--guide-level': guide.level }" aria-hidden="true" />
                  <span class="employee-label">{{ employee.name }}</span>
                  <span v-if="employee.isTeamLeader" class="employee-leader" aria-label="Jefe de equipo" title="Jefe de equipo">★</span>
                  <span v-if="employee.depth" class="visually-hidden"> · Nivel {{ employee.depth + 1 }} · Responsable: {{ employee.managerName }}</span>
                </div>
              </td>
              <td v-if="rangeOfDays.length" :colspan="rangeOfDays.length" class="timeline-cell">
                <div class="employee-timeline">
                  <div v-for="day in rangeOfDays" :key="day.format('YYYY-MM-DD')"
                    :class="{ 'weekend-cell': isWeekend(day) }" class="day-cell" />
                  <button v-for="(bar, barIndex) in employee.bars" :key="barIndex" type="button" class="holiday-bar"
                    :style="{
                      left: `calc(${bar.startDay} * var(--cell-width))`,
                      width: `calc(${bar.width} * var(--cell-width))`,
                      backgroundColor: bar.color,
                      color: bar.details.status === 'rejected' ? '#fff' : '#000'
                    }" @click="showHolidayDetails(bar, employee)" :title="bar.title">
                    {{ bar.text }}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

      </div>
    </div>
  </div>
  </div>
  <el-dialog v-model="dialogHolidayInfo" width="500px" center :show-close="false" class="holiday-info-dialog"
    @close="closeDialogHolidayInfo">
    <template #header>
    <div class="d-flex flex-row justify-content-start">
      <img src="@/assets/icons/hollidays.svg" alt="">
      <div class="title-text ml-4">
        Vacaciones
      </div>
    </div>
    </template>
    <template #default v-if="dialogHolidayInfo && selectedHoliday != null">
      <div class="dialog-content">
        <div class="field"><label>Empleado:</label><span>{{ selectedEmployee.name }}</span></div>
        <div class="field field-status">
          <label for="planning-holiday-status">Estado:</label>
          <TeamHolidayStatus v-if="actionable" id="planning-holiday-status" :holiday="selectedHoliday.details"
            :employee-name="selectedEmployee.name" class="holiday-detail-status" @change="requestStatusChange" />
          <span v-else>{{ planningStatusLabels[selectedHoliday.details.status] || 'Sin estado' }}</span>
        </div>
        <div class="field">
          <label>Vacaciones:</label>
          <span>{{ selectedHoliday.details.observations || '-' }}</span>
        </div>
        <div class="field">
          <label>Desde:</label>
          <span>{{ dayjs(selectedHoliday.details.fromDate).format('DD/MM/YYYY') }}</span>
        </div>
        <div class="field">
          <label>Hasta:</label>
          <span>{{ dayjs(selectedHoliday.details.toDate).format('DD/MM/YYYY') }}</span>
        </div>
      </div>
    </template>
    <template #footer>
      <el-button class="btn btn-block btn_red holiday-close"
        @click="closeDialogHolidayInfo">Cerrar</el-button>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.holiday-planning {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  max-height: 700px;
}

// Only the calendar scrolls; its legend stays above it on both axes.
.holiday-grid-container {
  width: 100%;
  min-height: 0;
  overflow: auto;
}

.holiday-legend { flex: 0 0 auto; display: flex; flex-wrap: wrap; gap: 12px 20px; margin-bottom: 12px; }
.holiday-legend span { display: inline-flex; align-items: center; gap: 6px; }
.holiday-legend i { width: 12px; height: 12px; border-radius: 50%; }

.table-wrapper {
  width: 100%;
  /* overflow: auto; */
}

.gantt-container {
  --cell-width: 50px;
  --row-height: 42px;
  /* Altura total de la fila */
  --month-header-height: 34px;
  --day-header-height: 50px;
  --employee-column-width: 200px;
  --bar-height: 30px;
  position: relative;
  width: max-content;
}

table {
  border-collapse: separate;
  border-spacing: 0;
  // Un ancho explícito activa table-layout: fixed y evita que los nombres
  // ensanchen la primera columna respecto al origen de las barras.
  width: calc(var(--employee-column-width) + var(--day-count) * var(--cell-width));
  table-layout: fixed;
}

.employee-col { width: var(--employee-column-width); }
.day-col { width: var(--cell-width); }

th,
td {
  border: 0;
  border-right: 1px solid #ccc;
  border-bottom: 1px solid #ccc;
  padding: 0;
  text-align: center;
  width: var(--cell-width);
  height: var(--row-height);
  box-sizing: border-box;
  overflow: hidden;
}

.employee-column {
  background-color: #f4f4f4;
  position: sticky;
  left: 0;
  z-index: 20 !important; // Aumentado para estar siempre por encima de las barras
  width: var(--employee-column-width);
}

.employee-name {
  background-color: #fff;
  font-weight: bold;
  position: sticky;
  left: 0;
  z-index: 10; // Aumentado para estar siempre por encima de las barras
  width: var(--employee-column-width);
  padding: 0;
}

.employee-name-content {
  padding: 6px 8px;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  height: calc(var(--row-height) - 1px);
  box-sizing: border-box;
  line-height: calc(var(--row-height) - 12px);
  background-color: #fff; // Asegura que el fondo sea sólido
}

.gantt-hierarchy { --employee-column-width: 320px; --row-height: 54px; --tree-padding: 16px; --tree-indent: 16px; }
.employee-name-content.employee-hierarchy { position: relative; display: flex; align-items: center; gap: 8px; padding: 8px 12px; padding-left: calc(var(--tree-padding) + var(--employee-depth) * var(--tree-indent)); line-height: 1.3; font-size: 13px; font-weight: 500; color: #34359e; }
.employee-hierarchy .employee-label { flex: 1; white-space: normal; overflow-wrap: anywhere; display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.employee-manager .employee-hierarchy { font-weight: 700; background: #f5f5ff; color: #474ce6; }
.employee-root .employee-hierarchy { border-top: 1px solid #e7e7f5; }
.employee-label { min-width: 0; overflow: hidden; text-overflow: ellipsis; }
.employee-leader { flex: 0 0 22px; height: 22px; display: inline-flex; align-items: center; justify-content: center; color: #d99a00; background: #fff8d4; border-radius: 7px; font-size: 13px; }
.employee-guide { position: absolute; top: 0; bottom: 0; left: calc(var(--tree-padding) + var(--guide-level) * var(--tree-indent)); width: 1px; }
.employee-guide { background: linear-gradient(#d4d4f5, #d4d4f5) top / 1px 50% no-repeat; }
.employee-guide.guide-continues { background: #d4d4f5; }
.employee-guide.guide-last::after { content: ''; position: absolute; top: calc(50% - 2px); left: -2px; width: 5px; height: 5px; border-radius: 50%; background: #8586ee; box-shadow: 0 0 0 2px #fff; }

.month-header {
  background-color: #eaeaea;
  font-weight: bold;
  height: var(--month-header-height);
}

.day-column {
  background-color: #f7f7f7;
  height: var(--day-header-height);
}

.day-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.2;
}

.day-number {
  font-weight: bold;
}

.day-name {
  font-size: 0.8em;
  color: gray;
}

.weekend-cell {
  background-color: #f0f0f0;
}

.employee-row {
  height: var(--row-height);
}

.timeline-cell {
  padding: 0;
  border: 0;
}

.employee-timeline {
  position: relative;
  display: grid;
  grid-template-columns: repeat(var(--day-count), var(--cell-width));
  height: var(--row-height);
  overflow: hidden;
}

.day-cell {
  height: var(--row-height);
  box-sizing: border-box;
  border-right: 1px solid #ccc;
  border-bottom: 1px solid #ccc;
}

.holiday-bar {
  border: 0;
  position: absolute;
  height: var(--bar-height);
  /* Calculamos la posición top para centrar perfectamente */
  top: calc((var(--row-height) - var(--bar-height)) / 2);
  border-radius: 20px;
  /* Reducimos el padding horizontal para que no afecte al ancho total */
  padding: 0 4px;
  display: flex;
  align-items: center;
  font-size: 0.9em;
  color: black;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  transition: opacity 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  /* Aseguramos que el padding y el borde no afecten al tamaño total */
  box-sizing: border-box;
  margin: 0;

  &:hover {
    opacity: 0.9;
  }
}

.sticky-header th {
  position: sticky;
  top: 0;
  z-index: 15;
}

/* Segunda fila del encabezado (los días) */
.sticky-header tr:nth-child(2) th {
  top: var(--month-header-height);
  z-index: 14;
}

// El contenido tiene scope propio; el contenedor de Element Plus se teleporta.
.dialog-content { display: flex; flex-direction: column; gap: 16px; padding: 16px 0; }
.field { display: flex; align-items: baseline; gap: 8px; }
.field label { font-weight: bold; flex: 0 0 90px; margin: 0; }
.field span { color: #333; overflow-wrap: anywhere; min-width: 0; }
.holiday-bar:focus-visible { outline: 2px solid #474ce6; outline-offset: -2px; }
.holiday-close { max-width: 200px; }
.holiday-detail-status { flex: 1; min-width: 0; }
.field-status { align-items: center; }
@media (max-width: 767px) {
  .gantt-hierarchy { --employee-column-width: min(180px, 44vw); --row-height: 60px; --tree-padding: 10px; --tree-indent: 10px; }
  .gantt-hierarchy .employee-hierarchy { gap: 5px; padding-top: 6px; padding-bottom: 6px; padding-right: 8px; font-size: 12px; }
  .gantt-hierarchy .employee-label { -webkit-line-clamp: 3; }
  .gantt-hierarchy .employee-leader { flex-basis: 18px; height: 18px; font-size: 11px; border-radius: 5px; }
  .field { flex-direction: column; gap: 4px; }
  .field-status { align-items: stretch; }
  .field label { flex-basis: auto; }
  .holiday-detail-status { flex: auto; }
}
</style>

<style lang="scss">
.holiday-info-dialog.el-dialog { max-width: calc(100vw - 24px); }
.holiday-info-dialog .el-dialog__body { padding: 16px 24px; }
</style>
